import React, { useEffect, useState } from 'react';
import { UserRound, Check, Loader2 } from 'lucide-react';
import { getMyAboutYou, updateMyAboutYou, type AboutYou } from '../../services/profileService';

/**
 * "About you" — OPTIONAL self-info the agent cast can reference warmly to feel personal (never
 * required, never surfaced as data). All fields optional; the star sign derives from the DOB
 * server-side. Simple load → edit → save; the same card style as the other profile sections.
 */
const FIELDS: { key: keyof AboutYou; label: string; placeholder: string }[] = [
  { key: 'location', label: 'Location', placeholder: 'e.g. Berlin' },
  { key: 'hobbies', label: 'Hobbies', placeholder: 'e.g. climbing, cooking, film photography' },
  { key: 'sports', label: 'Sports', placeholder: 'e.g. tennis, follow the NBA' },
  { key: 'favourite_food', label: 'Favourite food', placeholder: 'e.g. ramen' },
  { key: 'pets', label: 'Pet(s)', placeholder: 'e.g. a dog named Miso' },
  { key: 'favourite_song', label: 'Favourite song', placeholder: 'e.g. anything by Fleetwood Mac' },
  { key: 'favourite_movie', label: 'Favourite movie', placeholder: 'e.g. Spirited Away' },
];

const GENDERS = ['', 'Female', 'Male', 'Non-binary', 'Prefer not to say'];

const inputCls =
  'w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500';

const AboutYouSection: React.FC = () => {
  const [about, setAbout] = useState<AboutYou>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getMyAboutYou().then(setAbout).catch(() => { /* start empty */ }).finally(() => setLoading(false));
  }, []);

  const set = (key: keyof AboutYou, value: string) => { setAbout((a) => ({ ...a, [key]: value })); setSaved(false); };

  const save = async () => {
    setSaving(true); setSaved(false);
    try { const next = await updateMyAboutYou(about); setAbout(next); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
      <div className="flex items-center gap-2">
        <UserRound className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">About you</h3>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-secondary-400" />}
        {saved && !saving && <Check className="w-3.5 h-3.5 text-green-500" />}
      </div>
      <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
        Optional. Share a little about yourself and the agents will chat more personally — it's only used to make
        conversations warmer, never shown as data. Leave anything blank.
      </p>

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-secondary-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs text-secondary-500 dark:text-secondary-400">Gender</span>
              <select value={about.gender || ''} onChange={(e) => set('gender', e.target.value)} className={`${inputCls} mt-1`}>
                {GENDERS.map((g) => <option key={g} value={g}>{g || 'Prefer not to say'}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-secondary-500 dark:text-secondary-400">Agent warmth</span>
              <select value={about.attracted_to || ''} onChange={(e) => set('attracted_to', e.target.value)} className={`${inputCls} mt-1`}>
                <option value="">Default (from gender)</option>
                <option value="women">A little warmer with women</option>
                <option value="men">A little warmer with men</option>
                <option value="everyone">Warm with everyone</option>
                <option value="none">Keep it strictly professional</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                Date of birth {about.star_sign && <span className="text-secondary-400">· {about.star_sign}</span>}
              </span>
              <input type="date" value={about.dob || ''} onChange={(e) => set('dob', e.target.value)} className={`${inputCls} mt-1`} />
            </label>
            {FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className="text-xs text-secondary-500 dark:text-secondary-400">{f.label}</span>
                <input type="text" value={(about[f.key] as string) || ''} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} className={`${inputCls} mt-1`} />
              </label>
            ))}
          </div>

          <label className="block mt-4">
            <span className="text-xs text-secondary-500 dark:text-secondary-400">Short intro</span>
            <textarea value={about.bio || ''} onChange={(e) => set('bio', e.target.value)} rows={3}
              placeholder="A sentence or two about you — what you're into, what you're working on…"
              className={`${inputCls} mt-1 resize-none`} maxLength={500} />
          </label>

          <div className="mt-4 flex items-center gap-3">
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-4 py-2 text-sm font-medium text-white dark:text-secondary-900 hover:opacity-90 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
            </button>
            {saved && !saving && <span className="text-xs text-green-600 dark:text-green-400">Saved</span>}
          </div>
        </>
      )}
    </div>
  );
};

export default AboutYouSection;
